import { useGianoAccount } from "@/hooks/use-giano-account";
import { useGianoConnect } from "@/hooks/use-giano-connect";
import { useLogout } from "@/hooks/use-logout";
import { useAuthStore } from "@/lib/store";
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import GlobalLoader from "./GlobalLoader";
import { useApiQuery } from "@/hooks/use-api-query";
import { GetTenantUserChallenge } from "@/queries/get-tenant-user-challenge.query";
import { CompleteTenantUserChallenge } from "@/mutations/complete-tenant-user-challenge.mutation";
import { useApiMutation } from "@/hooks/use-api-mutation";
import { GetRootAdminChallenge } from "@/queries/get-root-admin-challenge.query";
import { CompleteRootAdminChallenge } from "@/mutations/complete-root-admin-challenge.mutation";
import { useToast } from "@/hooks/use-toast";
import { describeWebAuthnError } from "@/utils/error";

interface Props {
    children: ReactNode
}

export const GianoWatcher: FC<Props> = ({ children }) => {
    const user = useAuthStore(state => state.user)
    const [isReady, setIsReady] = useState(false)
    const { logout } = useLogout()
    const { toast } = useToast()
    const onAfterLogin = useCallback(() => {
        setIsReady(true)
    }, [])
    const onConnectError = useCallback((error: unknown) => {
        // Signing the person out with no word reads as a crash. When the passkey failure is one we
        // can explain, say so; the Toaster sits at the App root, so the message outlives the logout.
        // Anything else keeps the silent logout.
        const description = describeWebAuthnError(error)
        if (description) {
            toast({ variant: 'destructive', title: 'Signed out', description })
        }
        logout()
    }, [logout, toast])
    const { 
        data: tenantUserChallengeData = { challenge: undefined }, 
        error: errorFetchingTenantUserChallenge, 
        isLoading: isLoadingTenantUserChallenge,
        isSuccess: isSuccessTenantUserChallenge,
        refetch: refetchTenantUserChallenge 
    } = useApiQuery(GetTenantUserChallenge({}), { enabled: user?.type === 'tenant' })
    const { 
        data: rootAdminChallengeData = { challenge: undefined }, 
        error: errorFetchingRootAdminChallenge, 
        isLoading: isLoadingRootAdminChallenge, 
        refetch: refetchRootAdminChallenge 
    } = useApiQuery(GetRootAdminChallenge(), { enabled: user?.type === 'root_admin' })
    const challengeData = isSuccessTenantUserChallenge ? tenantUserChallengeData : rootAdminChallengeData
    const errorFetchingChallenge = errorFetchingTenantUserChallenge || errorFetchingRootAdminChallenge
    const isLoadingChallenge = isLoadingTenantUserChallenge || isLoadingRootAdminChallenge
    const refetchChallenge = refetchTenantUserChallenge || refetchRootAdminChallenge

    const challengeProps = useMemo(() => ({
        challenge: challengeData?.challenge || undefined,
        error: errorFetchingChallenge || undefined,
        isLoading: isLoadingChallenge,
        refetch: refetchChallenge,
    }), [challengeData?.challenge, errorFetchingChallenge, isLoadingChallenge, refetchChallenge])
    const { 
        mutateAsync: completeTenantUserChallenge, 
        error: errorCompletingTenantUserChallenge, 
        isPending: isPendingCompletingTenantUserChallenge 
    } = useApiMutation(CompleteTenantUserChallenge)
    const { 
        mutateAsync: completeRootAdminChallenge, 
        error: errorCompletingRootAdminChallenge, 
        isPending: isPendingCompletingRootAdminChallenge 
    } = useApiMutation(CompleteRootAdminChallenge)
    const completeChallenge = user?.type === 'tenant' ? completeTenantUserChallenge : completeRootAdminChallenge
    const errorCompletingChallenge = user?.type === 'tenant' ? errorCompletingTenantUserChallenge : errorCompletingRootAdminChallenge
    const isPendingCompletingChallenge = user?.type === 'tenant' ? isPendingCompletingTenantUserChallenge : isPendingCompletingRootAdminChallenge

    const completeChallengeProps = useMemo(() => ({
        completeChallenge: completeChallenge,
        error: errorCompletingChallenge || undefined,
        isPending: isPendingCompletingChallenge,
    }), [completeChallenge, errorCompletingChallenge, isPendingCompletingChallenge])

    const { isReadyToConnect, connect } = useGianoConnect({ challenge: challengeProps, completeChallenge: completeChallengeProps })
    const { address } = useGianoAccount()

    // The access token lives in memory only, so a user restored after a reload must sign in again
    // to get a fresh one even if the wallet itself is still connected.
    const isSupposedToReconnect = user != null && (!address || !user.accessToken)

    useEffect(() => {
        if (isSupposedToReconnect && !isReady && isReadyToConnect) {
            connect({ onSuccess: onAfterLogin, onError: onConnectError })
        }
    }, [isSupposedToReconnect, isReadyToConnect, connect, address, isReady, onAfterLogin, onConnectError])

    useEffect(() => {
        if (!isSupposedToReconnect) {
            setIsReady(true)
        }
    }, [isSupposedToReconnect])

    if (!isReady) {
        return <GlobalLoader />
    }

    return <>{children}</>
}